import React, { useCallback, useId } from 'react';
import { KitFontId, ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../../../common/types';
import { CatalogItem, SerializedKit, SerializedKitAsset } from '../../../../../redux/api/fymAPI.types';
import { useKitWebFonts } from '../../themes/kitWebFonts';
import { useKitLook } from './useKitLook';
import { useThemeStillSong } from './useThemeStillSong';
import { ThemePanelShell } from './ThemePanelShell';
import { ThemePanelBody } from './ThemePanelBody';
import { DraftThemePanel } from './DraftThemePanel';

const NO_PALETTE: string[] = [];
const NO_ASSETS: SerializedKitAsset[] = [];

export interface ThemePanelProps {
  accountId: number;
  item: CatalogItem;
  /** Null when nothing is made yet: the panel shows the default look, and a change makes the kit. */
  kit: SerializedKit | null;
  /** The current assets of the song: the stale note counts the ones in an older theme. */
  assets?: SerializedKitAsset[];
  /** The line next to the title. The Studio dialog says "Changes apply to this song." */
  hint?: string;
  /** "Show them" of the stale note. With no handler, the note does not show (the Studio dialog). */
  onShowStale?: () => void;
  /** The Studio dialog: no frame of its own. */
  isInDialog?: boolean;
  /**
   * The song page: true shows only what is saved (`ThemeSummary`) and the stale note. The Studio
   * dialog leaves it out: the panel is always open there.
   */
  isCollapsed?: boolean;
  /** "Edit theme" of the closed panel. */
  onExpand?: () => void;
  /** "Done", Save and Cancel of the open panel. */
  onCollapse?: () => void;
  /** The song page: the draft is not saved. The page guards a leave meanwhile. */
  onDirtyChange?: (isDirty: boolean) => void;
}

type InstantThemePanelProps = Omit<ThemePanelProps, 'isCollapsed' | 'onExpand' | 'onCollapse' | 'onDirtyChange'> & {
  assets: SerializedKitAsset[];
};

/** The always-open panel of the Studio dialog: each pick is one `PATCH .../look`; the still changes at once. */
const InstantThemePanelComponent: React.FC<InstantThemePanelProps> = ({
  accountId,
  item,
  kit,
  assets,
  hint,
  onShowStale,
  isInDialog = false,
}) => {
  const bodyId = useId();
  useKitWebFonts();
  const song = useThemeStillSong(item);
  const { look, saveAsDefault, isSaving, change, toggleSaveAsDefault } = useKitLook({
    accountId,
    catalogItemUuid: item.uuid,
    kit,
  });
  const pickTheme = useCallback((themeId: ReleaseThemeId) => change({ themeId }), [change]);
  const pickFont = useCallback((fontOverride: KitFontId | null) => change({ fontOverride }), [change]);
  const pickLogo = useCallback((logoFileId: number | null) => change({ logoFileId }), [change]);

  return (
    <ThemePanelShell
      accountId={accountId}
      item={item}
      kit={kit}
      assets={assets}
      hint={hint}
      onShowStale={onShowStale}
      isInDialog={isInDialog}
    >
      <ThemePanelBody
        accountId={accountId}
        bodyId={bodyId}
        artistName={item.artistName}
        look={look}
        palette={kit?.palette ?? NO_PALETTE}
        song={song}
        logoUrl={kit?.logoUrl ?? null}
        saveAsDefault={saveAsDefault}
        isSaving={isSaving}
        onPickTheme={pickTheme}
        onPickFont={pickFont}
        onPickLogo={pickLogo}
        onToggleDefault={toggleSaveAsDefault}
      />
    </ThemePanelShell>
  );
};

const InstantThemePanel = TypedMemo(InstantThemePanelComponent);

/**
 * The Theme panel of a song (design q2, q2b, q4).
 *
 * - The song page (collapse handlers given): a draft panel. A pick changes only the draft and the
 *   preview; Save sends the PATCH, Cancel brings the saved look back (`DraftThemePanel`).
 * - The Studio dialog (no collapse handlers): always open, and a pick saves at once, so "Make it"
 *   uses the look on screen (`useKitLook`). Every member may change it (D7).
 */
const ThemePanelComponent: React.FC<ThemePanelProps> = ({
  isCollapsed = false,
  onExpand,
  onCollapse,
  onDirtyChange,
  isInDialog = false,
  assets = NO_ASSETS,
  ...rest
}) => {
  if (onExpand !== undefined && onCollapse !== undefined) {
    return (
      <DraftThemePanel
        // eslint-disable-next-line react/jsx-props-no-spreading -- the same props, passed on
        {...rest}
        assets={assets}
        isCollapsed={isCollapsed}
        onExpand={onExpand}
        onCollapse={onCollapse}
        onDirtyChange={onDirtyChange}
        isInDialog={isInDialog}
      />
    );
  }
  // eslint-disable-next-line react/jsx-props-no-spreading -- the same props, passed on
  return <InstantThemePanel {...rest} assets={assets} isInDialog={isInDialog} />;
};

export const ThemePanel = TypedMemo(ThemePanelComponent);
